import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import type { PriceOfDay, PricesByDays } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { useAppContext } from 'Context/ReactAppContext';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { Datepicker } from 'Components/frameworks/datepicker/Datepicker';
import { themes as datepickerThemes } from 'Components/frameworks/datepicker/Datepicker.themes';
import { DayWithPrice } from 'Components/widgets/trip-date-picker/day-with-price/DayWithPrice';
import type { OpeningDate, PriceResolver } from './TripDatePicker.utils';
import {
  PRICE_COLOR_CLASS_NAMES,
  closedDaysFactory,
  dateToInt,
  firstSiblingOpenDayOfSelectedMonthFactory,
  flattenPrices,
  getDisabledDays,
  getPriceColor,
  maxDatepickerDate,
  minDatepickerDate,
  siblingOpenDaysFactory,
} from './TripDatePicker.utils';
import { themes, type TripDatePickerThemeKey } from './TripDatePicker.themes';
type Props = {
  className?: string;
  departureDate?: (number | null | undefined) | (moment$Moment | null | undefined);
  displayPrices?: boolean;
  duration?: number;
  hasInstalment?: boolean;
  isJoyview?: boolean;
  navbarElement?: ReactNode;
  navigationByYear?: boolean;
  offsetCalendar?: number | null | undefined;
  onDayMouseOver?: (arg0: Date | null | undefined) => unknown;
  onSelect?: (arg0: { from: Date | null | undefined; to: Date | null | undefined }) => unknown;
  openCalendarToDate?: (number | null | undefined) | (moment$Moment | null | undefined);
  optionsDuration: Array<number>;
  prices?: PricesByDays;
  product?: Product;
  openingDates?: Array<OpeningDate>;
  selectedBestPrice?: string | null | undefined;
  selectedDays: {
    from: Date | null | undefined;
    to: Date | null | undefined;
  };
  selectedDuration?: number;
  selectedMonth?: number;
  selectedRealPrice?: string | null | undefined;
  shouldAllowSelectingSiblingOpenDays?: boolean;
  theme?: TripDatePickerThemeKey;
  withLongDayFormat?: boolean;
  withPriceDetails?: boolean;
};
type SelectorState = {
  isBookableDatesLoading: boolean | null | undefined;
};

const selector = (state: ReduxState): SelectorState => ({
  isBookableDatesLoading: (state?.calendar as { bookableDatesLoading?: boolean | null | undefined })
    ?.bookableDatesLoading,
});

const Weekday = ({ weekday, className, localeUtils, locale }: any) => {
  const weekdayName = localeUtils.formatWeekdayLong(weekday, locale);
  return (
    <div className={className} title={weekdayName}>
      {weekdayName}
    </div>
  );
};

export const TripDatePicker = ({
  className,
  departureDate,
  duration,
  hasInstalment,
  isJoyview,
  navbarElement,
  navigationByYear,
  offsetCalendar,
  onDayMouseOver,
  onSelect,
  openCalendarToDate,
  openingDates,
  optionsDuration,
  prices,
  product,
  selectedBestPrice,
  selectedDays: { from, to },
  selectedDuration,
  selectedMonth,
  selectedRealPrice,
  shouldAllowSelectingSiblingOpenDays = false,
  theme = 'default',
  withLongDayFormat,
  withPriceDetails = false,
  ...props
}: Props) => {
  const appContext = useAppContext();
  const { isTablet } = useResponsive();
  const { displayPrices = isFeatureFlipped(appContext)('datePicker.displayPrices') } = props;
  const { isBookableDatesLoading } = useSelector(selector);

  const onMouseOver = (day: Date | null | undefined) => {
    onDayMouseOver && onDayMouseOver(day);
  };

  const pricesIds = Object.keys(prices || {});
  const flattenedPrices = prices ? flattenPrices(prices) : '';
  const hasPrices = pricesIds.length > 0;
  const doDisplayPricesInDatePicker = (hasPrices && displayPrices) || theme === 'compact';
  const bothDatesSelected = Boolean(from && to);
  const resolvePrice: PriceResolver = useCallback(
    (dayId: number) => get(prices, `[${dayId}]`, {}) as PriceOfDay,
    [flattenedPrices],
  );
  const { isSeller, locale } = appContext;
  const disabledDays = useMemo(() => {
    return getDisabledDays({
      from,
      to,
      prices,
      isSeller,
      optionsDuration,
      openingDates,
      resolvePrice,
      shouldAllowSelectingSiblingOpenDays,
    });
  }, [from, isSeller, optionsDuration.join(), flattenedPrices, to]);
  const dayIsClosed = useCallback(
    closedDaysFactory(
      resolvePrice,
      from,
      moment(to).subtract(1, 'day').toDate(),
      shouldAllowSelectingSiblingOpenDays,
    ),
    [from, to, flattenedPrices],
  );
  const modifiers = {
    [PRICE_COLOR_CLASS_NAMES.DARK_GREY]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.DARK_GREY,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.GREEN]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.GREEN,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.GREEN_OPACITY_45]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.GREEN_OPACITY_45,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.GREY]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.GREY,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.ORANGE]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.ORANGE,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.ORANGE_OPACITY_45]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.ORANGE_OPACITY_45,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.WHITE]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.WHITE,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    [PRICE_COLOR_CLASS_NAMES.TURQUOISE]: getPriceColor({
      classColor: PRICE_COLOR_CLASS_NAMES.TURQUOISE,
      from,
      hasPrices,
      resolvePrice,
      selectedMonth,
      to,
    }),
    '!text-lightGrey': disabledDays,
    disabledDays,
    firstSiblingOpenDayOfSelectedMonth: firstSiblingOpenDayOfSelectedMonthFactory(
      resolvePrice,
      selectedMonth,
    ),
    siblingOpenDays: siblingOpenDaysFactory(resolvePrice),
    ['pointer-events-none']: (day: Date) => {
      const dayIsSelected =
        from && to && dateToInt(day) >= dateToInt(from) && dateToInt(day) <= dateToInt(to);

      if (hasPrices && dayIsSelected) {
        return dayIsClosed(day);
      }
    },
  };
  const firstDateWithPrices: string | null | undefined = useMemo(() => {
    const today = dateToInt(new Date());
    const dayWithPrice = pricesIds.find(
      (pricesKey) =>
        dateToInt(moment(pricesKey, API_DATE_FORMAT).toDate()) > today &&
        prices &&
        prices[pricesKey].available,
    );
    return dayWithPrice || null;
  }, [JSON.stringify(flattenedPrices)]);
  const firstDateOpened: string | null | undefined = useMemo(() => {
    if (!Array.isArray(openingDates) || openingDates.length === 0) {
      return null;
    }

    const [{ opening }] = openingDates;
    return opening;
  }, [JSON.stringify(openingDates)]);
  const { thDay } = themes({
    isTablet,
  })[theme];
  // If bookable dates are loading only define current or selected day as range
  const datesRange = isBookableDatesLoading
    ? {
        min: moment(from).toDate(),
        max: moment(from).toDate(),
      }
    : {
        min: minDatepickerDate(offsetCalendar),
        max: maxDatepickerDate(),
      };
  const [openCalendarDate, setOpenCalendarDate] =
    useState<Props['openCalendarToDate']>(openCalendarToDate);
  useEffect(() => {
    setOpenCalendarDate(openCalendarToDate);
  }, [moment(openCalendarToDate).format(API_DATE_FORMAT)]);
  useEffect(() => {
    if (from) {
      setOpenCalendarDate(from as unknown as Props['openCalendarToDate']);
    }
  }, [moment(from).format(API_DATE_FORMAT)]);
  const initialMonth: Date = useMemo(() => {
    return moment(
      openCalendarDate ||
        from ||
        firstDateWithPrices ||
        firstDateOpened ||
        minDatepickerDate(offsetCalendar),
    ).toDate();
  }, [
    moment(openCalendarDate).format(API_DATE_FORMAT),
    moment(from).format(API_DATE_FORMAT),
    moment(firstDateWithPrices).format(API_DATE_FORMAT),
    moment(firstDateOpened).format(API_DATE_FORMAT),
    offsetCalendar,
  ]);
  return (
    <Datepicker
      hasRangeMode
      className={className}
      selectedDays={{
        from,
        to,
      }}
      disabledDays={disabledDays}
      customModifiers={modifiers}
      hasNavigationByYear={navigationByYear}
      minDate={datesRange.min}
      maxDate={datesRange.max}
      navbarElement={navbarElement}
      initialMonth={initialMonth}
      onSelect={onSelect as React.ComponentProps<typeof Datepicker>['onSelect']}
      onMouseOver={onMouseOver}
      theme={theme}
      shouldHoverSiblingOpenDays={shouldAllowSelectingSiblingOpenDays}
      weekdayElement={withLongDayFormat ? <Weekday /> : undefined}
      {...(doDisplayPricesInDatePicker
        ? asWidgetProps<Partial<React.ComponentProps<typeof Datepicker>>>({
            // eslint-disable-next-line clubmed/no-fake-component
            renderDay: (day: Date, dayModifiers) => {
              const datepickerTheme = datepickerThemes[theme as TripDatePickerThemeKey];
              const shouldDisplayPrice =
                theme === 'compact'
                  ? hasPrices && !dayModifiers[datepickerTheme.thDayPicker.selected]
                  : hasPrices && !to && !moment(from).isSame(day, 'day');
              const price = resolvePrice(dateToInt(day));
              return (
                <DayWithPrice
                  {...asWidgetProps<React.ComponentProps<typeof DayWithPrice>>({
                    day,
                    duration,
                    hasInstalment,
                    hasPrices,
                    isSelectable: Boolean(
                      !dayModifiers.disabledDays && from && !to && !moment(day).isSame(from, 'day'),
                    ),
                    isSelected: Boolean(dayModifiers[datepickerTheme.thDayPicker.selected]),
                    isLoading:
                      departureDate && isBookableDatesLoading && moment(day).isAfter(departureDate)
                        ? true
                        : false,
                    isJoyview,
                    locale,
                    price,
                    product,
                    selectedBestPrice,
                    selectedDuration,
                    selectedRealPrice,
                    isHighlighted:
                      (dayModifiers.today ||
                        price.isBestPrice ||
                        price.lastAvailability ||
                        price.siblingOpen) &&
                      !dayModifiers[datepickerTheme.thDayPicker.selected],
                    shouldDisplayDetails:
                      withPriceDetails &&
                      !!dayModifiers[datepickerTheme.thDayPicker.selected] &&
                      !from &&
                      !to,
                    shouldDisplayDoubleSeasonButton:
                      dayModifiers.firstSiblingOpenDayOfSelectedMonth,
                    shouldDisplayPrice: shouldDisplayPrice,
                    shouldDisplaySelectedDepartureDateMessage:
                      !!from && !to && moment(from).isSame(day, 'day'),
                    shouldDisplaySelectedDepartureDateMessageWithPrice:
                      bothDatesSelected && moment(from).isSame(day, 'day'),
                    shouldDisplaySelectedReturningDateMessage:
                      bothDatesSelected && moment(to).isSame(day, 'day'),
                    shouldDisplayStatusMessage: !from,
                    withPriceDetails,
                    theme,
                  })}
                />
              );
            },
            datePickerClassNames: {
              day: thDay,
            },
          })
        : null)}
    />
  );
};
